import React, { useState } from 'react';
import { Sun, Moon, Monitor, ChevronDown } from '../utils/icons';
import { useTheme } from '../contexts/ThemeContext';

const ThemeToggle: React.FC = () => {
  const { theme, actualTheme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const themes = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor }
  ] as const;

  const currentTheme = themes.find(t => t.value === theme);
  const CurrentIcon = currentTheme?.icon || Sun;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="
          flex items-center space-x-2 px-3 py-2 rounded-lg
          bg-white dark:bg-gray-800 
          border border-gray-200 dark:border-gray-700
          text-gray-700 dark:text-gray-300
          hover:bg-gray-50 dark:hover:bg-gray-700
          transition transform duration-200 hover:scale-105 active:scale-95
        "
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls="theme-toggle-listbox"
        title="Toggle theme"
      >
        <span className={`transition-transform duration-300 ${actualTheme === 'dark' ? 'rotate-180' : ''}`}>
          <CurrentIcon className="h-4 w-4" />
        </span>
        <span className="text-sm font-medium">{currentTheme?.label}</span>
        <span className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronDown className="h-3 w-3" />
        </span>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 z-40"
            role="presentation"
            aria-hidden="true"
          />

          {/* Dropdown */}
          <div
            className="
              absolute top-full left-0 mt-2 z-50
              bg-white dark:bg-gray-800
              border border-gray-200 dark:border-gray-700
              rounded-lg shadow-lg overflow-hidden
              min-w-[120px]
              animate-fade-in-down
            "
            role="listbox"
            id="theme-toggle-listbox"
            aria-label="Select theme"
          >
            {themes.map((themeOption) => {
              const Icon = themeOption.icon;
              const isSelected = theme === themeOption.value;
              
              return (
                <button
                  key={themeOption.value}
                  onClick={() => {
                    setTheme(themeOption.value);
                    setIsOpen(false);
                  }}
                  className={`
                    w-full flex items-center space-x-3 px-4 py-3 text-sm
                    transition-colors duration-150
                    ${isSelected 
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300' 
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }
                  `}
                  role="option"
                  aria-selected={isSelected ? 'true' : 'false'}
                >
                  <span className={`${isSelected ? 'scale-110' : ''} transition-transform duration-300`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="font-medium">{themeOption.label}</span>
                  {isSelected && (
                    <span className="ml-auto w-2 h-2 bg-primary-500 rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default ThemeToggle;
